import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { subMonths, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from '@/components/ui/popover';
import { EmptyState } from '@/components/EmptyState';
import {
  ArrowUpDown,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  Filter,
  Mail,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  adminApi,
  OrgSummary,
  BatchSummary,
  AdminUserRow,
  AdminUserDetail,
  UserMembership,
  InviteUserRequest,
  InviteUserResponse,
  BulkInviteResponse,
  SkippedInvite,
} from '@/lib/api/admin';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatTimestamp } from '@/lib/date';

export const Route = createFileRoute('/admin/users')({
  validateSearch: (search: Record<string, unknown>) => ({
    status: (search.status as string) || undefined,
    org:    (search.org    as string) || undefined,
    batch:  (search.batch  as string) || undefined,
    tab:    (search.tab    as string) || undefined,
  }),
  component: UsersPage,
});

const labelCls = 'block text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1.5';


export function parseEmails(raw: string): { emails: string[]; duplicatesRemoved: number } {
  const tokens = raw
    .split(/[\n,;]+/)
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.includes('@'));
  const unique = [...new Set(tokens)];
  return { emails: unique, duplicatesRemoved: tokens.length - unique.length };
}

// ── Role tabs ────────────────────────────────────────────────────────────────

export type TabKey = 'students' | 'trainers' | 'org_admins';

export const TAB_ORDER: TabKey[] = ['students', 'trainers', 'org_admins'];

export const TAB_ROLE: Record<TabKey, string> = {
  students: 'student',
  trainers: 'trainer',
  org_admins: 'org_admin',
};

export const TAB_LABEL: Record<TabKey, string> = {
  students: 'Students',
  trainers: 'Trainers',
  org_admins: 'Org Admins',
};

export function normalizeTab(raw: string | undefined): TabKey {
  return TAB_ORDER.includes(raw as TabKey) ? (raw as TabKey) : 'students';
}

export function countByRole(users: AdminUserRow[]): Record<TabKey, number> {
  const counts: Record<TabKey, number> = { students: 0, trainers: 0, org_admins: 0 };
  for (const t of TAB_ORDER) {
    counts[t] = users.filter((u) => u.role === TAB_ROLE[t]).length;
  }
  return counts;
}

export function filterUsersForTab(
  users: AdminUserRow[],
  tab: TabKey,
  search: string,
  status: string,
): AdminUserRow[] {
  const q = search.toLowerCase();
  const role = TAB_ROLE[tab];
  return users.filter(
    (u) =>
      u.role === role &&
      (u.username.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)) &&
      (!status || u.status === status),
  );
}

// ── Invite User Modal ────────────────────────────────────────────────────────

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  orgs: OrgSummary[];
  initialOrgId?: string;
  initialBatchId?: string;
  initialBatches?: BatchSummary[];
}

export function InviteUserModal({ open, onClose, orgs, initialOrgId = '', initialBatchId = '', initialBatches = [] }: InviteUserModalProps) {
  const [tab, setTab] = useState<'single' | 'bulk'>('single');

  // ── Single tab state ──────────────────────────────────────────────────────
  const [email, setEmail]       = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole]         = useState<InviteUserRequest['role'] | ''>('');
  const [orgId, setOrgId]     = useState(initialOrgId);
  const [batchId, setBatchId] = useState(initialBatchId);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [inviteURL, setInviteURL] = useState('');
  const [inviteContext, setInviteContext] = useState<{ email: string; orgName: string; batchName: string } | null>(null);
  const [modalBatches, setModalBatches] = useState<BatchSummary[]>([]);
  const urlInputRef = useRef<HTMLInputElement>(null);

  // ── Bulk tab state ────────────────────────────────────────────────────────
  const [bulkOrgId, setBulkOrgId]     = useState(initialOrgId);
  const [bulkBatchId, setBulkBatchId] = useState(initialBatchId);
  const [bulkBatches, setBulkBatches] = useState<BatchSummary[]>(initialBatches);
  const [parsedEmails, setParsedEmails] = useState<string[]>([]);
  const [fileName, setFileName]       = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult]   = useState<BulkInviteResponse | null>(null);
  const [skippedOpen, setSkippedOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inputMode, setInputMode]           = useState<'paste' | 'csv'>('paste');
  const [pasteText, setPasteText]           = useState('');
  const [duplicatesRemoved, setDuplicatesRemoved] = useState(0);

  useEffect(() => {
    if (open) {
      setOrgId(initialOrgId);
      setBatchId(initialBatchId);
      setModalBatches(initialBatches);
      if (initialBatchId) setRole('student');
      setBulkOrgId(initialOrgId);
      setBulkBatchId(initialBatchId);
      setBulkBatches(initialBatches);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!orgId) { setModalBatches([]); return; }
    adminApi.listBatches(orgId)
      .then(setModalBatches)
      .catch(() => toast.error('Failed to load batches'));
  }, [orgId]);

  useEffect(() => {
    if (!bulkOrgId) { setBulkBatches([]); return; }
    adminApi.listBatches(bulkOrgId)
      .then(setBulkBatches)
      .catch(() => toast.error('Failed to load batches'));
  }, [bulkOrgId]);

  function reset() {
    setTab('single');
    setEmail(''); setUsername(''); setRole(''); setOrgId(initialOrgId); setBatchId(initialBatchId);
    setLoading(false); setError(''); setInviteURL(''); setInviteContext(null);
    setBulkOrgId(initialOrgId); setBulkBatchId(initialBatchId);
    setParsedEmails([]); setFileName(''); setBulkLoading(false); setBulkResult(null);
    setSkippedOpen(false);
    setInputMode('paste');
    setPasteText('');
    setDuplicatesRemoved(0);
  }

  function handleClose() { reset(); onClose(); }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!role) return;
    setError('');
    setLoading(true);
    if (role === 'org_admin' && !orgId) { setError('Select an organization'); setLoading(false); return; }
    if (role === 'student' && (!orgId || !batchId)) { setError('Select an organization and batch'); setLoading(false); return; }
    const req: InviteUserRequest = { email, role, ...(username.trim() && { username: username.trim() }) };
    if (role === 'org_admin') req.org_id = orgId;
    if (role === 'student')   req.batch_id = batchId;
    try {
      const res: InviteUserResponse = await adminApi.inviteUser(req);
      const orgName   = orgs.find((o) => o.id === orgId)?.name ?? '';
      const batchName = modalBatches.find((b) => b.id === batchId)?.name ?? '';
      setInviteURL(res.invite_url);
      setInviteContext({ email, orgName, batchName });
      toast.success('Invite sent');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send invite';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  function copyURL() {
    urlInputRef.current?.select();
    navigator.clipboard.writeText(inviteURL);
    toast.success('Copied to clipboard');
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = (ev.target?.result as string) ?? '';
      const { emails, duplicatesRemoved: dupes } = parseEmails(
        text.split(/\r?\n/).map((l) => l.split(',')[0]).join('\n')
      );
      setParsedEmails(emails);
      setDuplicatesRemoved(dupes);
      setPasteText('');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
      setFileName('');
    };
    reader.readAsText(file);
  }

  async function handleBulkSubmit() {
    if (!bulkBatchId || parsedEmails.length === 0) return;
    if (parsedEmails.length > 500) {
      toast.error('CSV cannot exceed 500 emails');
      return;
    }
    setBulkLoading(true);
    try {
      const result = await adminApi.bulkInvite(bulkBatchId, parsedEmails);
      setBulkResult(result);
      if (result.sent.length > 0) toast.success(`${result.sent.length} invites sent`);
    } catch {
      toast.error('Failed to send bulk invites');
    } finally {
      setBulkLoading(false);
    }
  }

  function handlePasteChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const raw = e.target.value;
    setPasteText(raw);
    const { emails, duplicatesRemoved: dupes } = parseEmails(raw);
    setParsedEmails(emails);
    setDuplicatesRemoved(dupes);
  }

  function downloadTemplate() {
    const content = 'email\nstudent1@college.edu\nstudent2@college.edu\nstudent3@college.edu\n';
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invite-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-xl p-0 gap-0">
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="text-[15px] font-semibold">Invite User</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'single' | 'bulk')} className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-none border-b border-border bg-transparent p-0 h-auto">
            <TabsTrigger
              value="single"
              onClick={() => setTab('single')}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 text-[13px] font-medium focus-visible:outline-none focus-visible:border-b-primary/50"
            >
              Single
            </TabsTrigger>
            <TabsTrigger
              value="bulk"
              onClick={() => setTab('bulk')}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 text-[13px] font-medium focus-visible:outline-none focus-visible:border-b-primary/50"
            >
              Bulk CSV
            </TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="m-0 outline-none" tabIndex={-1}>
            {inviteURL ? (
              <div className="space-y-4 px-5 py-5">
                <p className="text-sm font-medium text-emerald-600">Invite sent</p>
                {inviteContext && (
                  <div className="rounded-lg bg-muted/40 px-3.5 py-2.5 text-[13px]">
                    <span className="font-medium text-foreground">{inviteContext.email}</span>
                    {inviteContext.batchName && (
                      <span className="text-muted-foreground"> → <span className="text-foreground">{inviteContext.batchName}</span></span>
                    )}
                    {inviteContext.orgName && (
                      <span className="text-muted-foreground"> · {inviteContext.orgName}</span>
                    )}
                  </div>
                )}
                <p className="text-[13px] text-muted-foreground">Share this link if the email doesn&apos;t arrive:</p>
                <div className="flex gap-2">
                  <Input ref={urlInputRef} readOnly value={inviteURL} aria-label="Invite URL" className="flex-1 truncate" />
                  <Button type="button" variant="outline" onClick={copyURL} className="gap-1.5">
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </Button>
                </div>
                <DialogFooter>
                  <Button onClick={handleClose} className="bg-primary hover:bg-primary/90 text-primary-foreground">Done</Button>
                </DialogFooter>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 px-5 py-5">
                <div>
                  <label htmlFor="invite-email" className={labelCls}>Email</label>
                  <Input id="invite-email" aria-label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
                </div>
                <div>
                  <label htmlFor="invite-username" className={labelCls}>Username <span className="normal-case text-muted-foreground font-normal">(optional)</span></label>
                  <Input id="invite-username" aria-label="Username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. rahul_trainer" />
                </div>
                <div>
                  <label htmlFor="invite-role" className={labelCls}>Role</label>
                  <Select
                    value={role || undefined}
                    onValueChange={(v) => {
                      const next = v as InviteUserRequest['role'];
                      setRole(next);
                      if (next === 'trainer') { setOrgId(''); setBatchId(''); }
                      if (next === 'org_admin') setBatchId('');
                    }}
                  >
                    <SelectTrigger id="invite-role" aria-label="Role">
                      <SelectValue placeholder="Select role…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="org_admin">Org Admin</SelectItem>
                      <SelectItem value="trainer">Trainer</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {role !== 'trainer' && (
                  <div>
                    <label htmlFor="invite-org" className={labelCls}>Organization</label>
                    <Select value={orgId || undefined} onValueChange={(v) => { setOrgId(v); setBatchId(''); }}>
                      <SelectTrigger id="invite-org" aria-label="Organization">
                        <SelectValue placeholder="Select organization…" />
                      </SelectTrigger>
                      <SelectContent>
                        {orgs.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {role === 'student' && (
                  <div>
                    <label htmlFor="invite-batch" className={labelCls}>Batch</label>
                    <Select value={batchId || undefined} onValueChange={setBatchId} disabled={!orgId}>
                      <SelectTrigger id="invite-batch" aria-label="Batch">
                        <SelectValue placeholder={orgId ? 'Select batch…' : 'Select org first…'} />
                      </SelectTrigger>
                      <SelectContent>
                        {modalBatches.map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {error && <p role="alert" className="rounded-lg bg-red-50 px-3.5 py-2 text-[13px] text-red-600 dark:bg-red-950/40 dark:text-red-400">{error}</p>}
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
                  <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                    {loading ? 'Sending…' : 'Send Invite'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </TabsContent>

          <TabsContent value="bulk" className="m-0 outline-none" tabIndex={-1}>
            <div className="space-y-4 px-5 py-5">
              {bulkResult ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-[13px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                    {bulkResult.sent.length} invite{bulkResult.sent.length !== 1 ? 's' : ''} sent
                  </div>
                  {bulkResult.skipped.length > 0 && (
                    <div className="rounded-lg border border-border">
                      <button
                        type="button"
                        onClick={() => setSkippedOpen((o) => !o)}
                        className="flex w-full items-center justify-between px-3.5 py-2.5 text-[13px] font-medium text-muted-foreground hover:bg-muted/50"
                      >
                        {bulkResult.skipped.length} skipped
                        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', skippedOpen && 'rotate-180')} />
                      </button>
                      {skippedOpen && (
                        <div className="border-t border-border divide-y divide-border/50">
                          {bulkResult.skipped.map((s: SkippedInvite) => (
                            <div key={s.email} className="flex items-center justify-between px-3.5 py-2 text-[12px]">
                              <span className="text-foreground truncate">{s.email}</span>
                              <span className="ml-3 shrink-0 text-muted-foreground">{s.reason}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <DialogFooter>
                    <Button onClick={handleClose} className="bg-primary hover:bg-primary/90 text-primary-foreground">Done</Button>
                  </DialogFooter>
                </div>
              ) : (
                <>
                  {/* Org + Batch row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="bulk-org" className={labelCls}>Organization</label>
                      <Select value={bulkOrgId || undefined} onValueChange={(v) => { setBulkOrgId(v); setBulkBatchId(''); }}>
                        <SelectTrigger id="bulk-org" aria-label="Organization">
                          <SelectValue placeholder="Select organization…" />
                        </SelectTrigger>
                        <SelectContent>
                          {orgs.map((o) => (
                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label htmlFor="bulk-batch" className={labelCls}>Batch</label>
                      <Select value={bulkBatchId || undefined} onValueChange={setBulkBatchId} disabled={!bulkOrgId}>
                        <SelectTrigger id="bulk-batch" aria-label="Batch">
                          <SelectValue placeholder={bulkOrgId ? 'Select batch…' : 'Select org first…'} />
                        </SelectTrigger>
                        <SelectContent>
                          {bulkBatches.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Input mode toggle */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className={labelCls + ' mb-0'}>Emails</label>
                      <div className="flex gap-1.5 ml-0">
                        <button
                          type="button"
                          onClick={() => { setInputMode('paste'); setFileName(''); setParsedEmails([]); setDuplicatesRemoved(0); }}
                          className={cn(
                            'px-3 py-1 rounded text-[12px] font-medium transition-colors',
                            inputMode === 'paste'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80',
                          )}
                        >
                          Paste
                        </button>
                        <button
                          type="button"
                          onClick={() => { setInputMode('csv'); setPasteText(''); setParsedEmails([]); setDuplicatesRemoved(0); }}
                          className={cn(
                            'px-3 py-1 rounded text-[12px] font-medium transition-colors',
                            inputMode === 'csv'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80',
                          )}
                        >
                          Upload CSV
                        </button>
                      </div>
                      <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); downloadTemplate(); }}
                        className="ml-auto text-[11px] text-primary hover:underline flex items-center gap-1"
                        aria-label="Download CSV template"
                      >
                        <Download className="h-3 w-3" /> template.csv
                      </a>
                    </div>

                    {inputMode === 'paste' ? (
                      <textarea
                        placeholder="Paste emails here — one per line, or comma/semicolon separated"
                        value={pasteText}
                        onChange={handlePasteChange}
                        className="w-full h-32 rounded-lg border border-border bg-background px-3 py-2.5 text-[13px] text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
                      />
                    ) : (
                      <>
                        <input
                          id="bulk-csv-input"
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          className="hidden"
                          onChange={handleFileChange}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full rounded-lg border-2 border-dashed border-border px-4 py-6 text-center text-[13px] text-muted-foreground hover:border-primary/50 hover:bg-muted/30 transition-colors"
                        >
                          {fileName
                            ? <span className="font-medium text-foreground">{fileName}</span>
                            : 'Click to browse for a .csv file'}
                        </button>
                      </>
                    )}

                    {/* Preview line */}
                    {parsedEmails.length > 0 && (
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[12px]">
                        <span className="text-emerald-600">{parsedEmails.length} will be invited</span>
                        {duplicatesRemoved > 0 && (
                          <span className="text-muted-foreground">{duplicatesRemoved} duplicate{duplicatesRemoved !== 1 ? 's' : ''} removed</span>
                        )}
                      </div>
                    )}
                  </div>

                  <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
                    <Button
                      type="button"
                      onClick={handleBulkSubmit}
                      disabled={bulkLoading || !bulkBatchId || parsedEmails.length === 0}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      {bulkLoading
                        ? 'Sending…'
                        : parsedEmails.length > 0
                          ? `Send ${parsedEmails.length} Invite${parsedEmails.length !== 1 ? 's' : ''}`
                          : 'Send Invites'}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>

      </DialogContent>
    </Dialog>
  );
}

// ── User Detail Panel ─────────────────────────────────────────────────────────

function UserDetailPanel({ user, onClose, onAssigned }: { user: AdminUserRow | null; onClose: () => void; onAssigned: () => void; }) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignOpen, setAssignOpen]       = useState(false);
  const [assignOrgs, setAssignOrgs]       = useState<OrgSummary[]>([]);
  const [assignOrgId, setAssignOrgId]     = useState('');
  const [assignBatches, setAssignBatches] = useState<BatchSummary[]>([]);
  const [assignBatchId, setAssignBatchId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError]     = useState('');

  function resetAssign() {
    setAssignOpen(false);
    setAssignOrgId('');
    setAssignBatchId('');
    setAssignBatches([]);
    setAssignError('');
  }

  useEffect(() => {
    if (!user) { setDetail(null); return; }
    setLoading(true);
    resetAssign();
    adminApi.getUser(user.user_id)
      .then(setDetail)
      .catch(() => toast.error('Failed to load user details'))
      .finally(() => setLoading(false));
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // For students: derive their org from existing memberships and lock the picker to it.
  const studentOrgId = detail?.role === 'student'
    ? (detail.memberships.find((m) => !['dropped', 'completed', 'cancelled', 'ended'].includes(m.status))?.org_id ?? '')
    : '';

  useEffect(() => {
    if (!assignOpen) return;
    adminApi.listOrgs().then(setAssignOrgs).catch(() => toast.error('Failed to load organizations'));
    // Auto-select the student's org so only batches from their org are shown.
    if (studentOrgId) setAssignOrgId(studentOrgId);
  }, [assignOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAssignBatches([]);
    setAssignBatchId('');
    if (!assignOrgId) return;
    adminApi.listBatches(assignOrgId).then(setAssignBatches).catch(() => toast.error('Failed to load batches'));
  }, [assignOrgId]);

  const activeBatchIds = new Set(
    detail?.memberships
      .filter((m) => !['dropped', 'completed', 'cancelled', 'ended'].includes(m.status))
      .map((m) => m.batch_id) ?? [],
  );

  const alreadyInBatch = assignBatchId ? activeBatchIds.has(assignBatchId) : false;
  const availableBatches = assignBatches.filter((b) => !activeBatchIds.has(b.id));

  async function handleAssign() {
    if (!detail || !assignBatchId) return;
    setAssignError('');
    setAssignLoading(true);
    try {
      if (detail.role === 'trainer') {
        await adminApi.assignTrainer(assignBatchId, { user_id: detail.user_id });
      } else {
        await adminApi.enrollStudent(assignBatchId, { user_id: detail.user_id });
      }
      toast.success('Assigned successfully');
      const updated = await adminApi.getUser(detail.user_id);
      setDetail(updated);
      resetAssign();
      onAssigned();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setAssignError(msg || 'Failed to assign user to batch');
    } finally {
      setAssignLoading(false);
    }
  }

  const initials = detail
    ? (detail.username || detail.email).slice(0, 2).toUpperCase()
    : '';

  return (
    <>
      {user && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        className={cn(
          'fixed right-0 top-0 z-50 h-full w-[380px] bg-white shadow-xl border-l border-border flex flex-col transform transition-transform duration-200 ease-out',
          user ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5 shrink-0">
          <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">User Details</h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close panel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Delete confirmation banner */}
        {deleteConfirm && (
          <div className="bg-red-50 border-b border-red-200 px-5 py-3.5 shrink-0">
            <p className="text-[13px] font-medium text-red-700">Delete this user?</p>
            <p className="text-[12px] text-red-600 mt-0.5">Their account will be deactivated and all active enrollments revoked. History is preserved.</p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                className="h-7 text-[12px] bg-red-600 hover:bg-red-700 text-white"
                disabled={deleteLoading}
                onClick={async () => {
                  if (!detail) return;
                  setDeleteLoading(true);
                  try {
                    await adminApi.deleteUser(detail.user_id);
                    toast.success('User deleted');
                    onAssigned();
                    onClose();
                  } catch {
                    toast.error('Failed to delete user');
                    setDeleteLoading(false);
                  }
                }}
              >
                {deleteLoading ? 'Deleting…' : 'Yes, delete'}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-[12px]" onClick={() => setDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-44" />
                </div>
              </div>
              <Skeleton className="h-3 w-24 mt-4" />
              <Skeleton className="h-px w-full" />
              {[1, 2].map((i) => <Skeleton key={i} className="h-12 w-full rounded-none" />)}
            </div>
          ) : detail ? (
            <>
              {/* Identity block */}
              <div className="px-5 py-5 flex items-start gap-3.5 border-b border-border">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[14px] font-bold shrink-0 select-none">
                  {initials}
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-[14px] font-semibold text-foreground leading-tight truncate">
                    {detail.username || <span className="text-muted-foreground italic font-normal">No username</span>}
                  </p>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(detail.email); toast.success('Email copied'); }}
                    className="flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground group mt-0.5 max-w-full"
                  >
                    <span className="truncate">{detail.email}</span>
                    <Copy className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
                  </button>
                  <div className="flex items-center gap-2.5 mt-2">
                    <RoleBadge role={detail.role} />
                    <span className="text-[11px] text-muted-foreground">Joined {formatTimestamp(detail.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Memberships */}
              <div>
                <div className="flex items-center justify-between px-5 pt-4 pb-2">
                  <span className={labelCls + ' mb-0'}>
                    Batch Memberships
                    {detail.memberships.length > 0 && (
                      <span className="ml-1 normal-case font-normal">({detail.memberships.length})</span>
                    )}
                  </span>
                  {detail.role !== 'org_admin' && !assignOpen && (
                    <button
                      type="button"
                      onClick={() => setAssignOpen(true)}
                      className="flex items-center gap-1 text-[12px] text-primary hover:underline"
                    >
                      <Plus className="h-3 w-3" /> Assign
                    </button>
                  )}
                </div>

                {detail.memberships.length === 0 ? (
                  <div className="px-5 py-3">
                    <p className="text-[13px] text-muted-foreground italic">No batch memberships</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/60">
                    {detail.memberships.map((m: UserMembership, i: number) => (
                      <div key={i} className="px-5 py-3 hover:bg-muted/30 transition-colors group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-foreground leading-tight truncate">{m.batch_name}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{m.org_name}</p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <StatusDisplay status={m.status} />
                            {!['dropped', 'completed', 'cancelled', 'ended'].includes(m.status) && (
                              <button
                                type="button"
                                title="Remove from batch"
                                onClick={async () => {
                                  try {
                                    if (m.role === 'trainer') {
                                      await adminApi.revokeAssignment(detail.user_id, m.batch_id);
                                    } else {
                                      await adminApi.revokeEnrollment(detail.user_id, m.batch_id);
                                    }
                                    toast.success('Removed from batch');
                                    const updated = await adminApi.getUser(detail.user_id);
                                    setDetail(updated);
                                    onAssigned();
                                  } catch {
                                    toast.error('Failed to remove from batch');
                                  }
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 text-muted-foreground hover:text-red-500 hover:bg-red-50"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <RoleBadge role={m.role} />
                          <span className="text-[11px] text-muted-foreground">since {formatTimestamp(m.joined_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {assignOpen && (
                  <div className="mx-5 mt-3 mb-4 rounded-lg border border-border bg-muted/30 p-3.5 space-y-3">
                    <div>
                      <label className={labelCls}>Organization</label>
                      <Select
                        value={assignOrgId || undefined}
                        onValueChange={(v) => { setAssignOrgId(v); setAssignError(''); }}
                        disabled={!!studentOrgId}
                      >
                        <SelectTrigger className="h-8 text-[13px]">
                          <SelectValue placeholder="Select organization…" />
                        </SelectTrigger>
                        <SelectContent>
                          {assignOrgs.map((o) => (
                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={labelCls}>Batch</label>
                      <Select
                        value={assignBatchId || undefined}
                        onValueChange={(v) => { setAssignBatchId(v); setAssignError(''); }}
                        disabled={!assignOrgId}
                      >
                        <SelectTrigger className="h-8 text-[13px]">
                          <SelectValue placeholder={assignOrgId ? 'Select batch…' : 'Select org first…'} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableBatches.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {(alreadyInBatch || assignError) && (
                      <p className="text-[12px] text-red-600">
                        {alreadyInBatch ? 'Already a member of this batch' : assignError}
                      </p>
                    )}
                    <div className="flex items-center gap-2 justify-end">
                      <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={resetAssign}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 text-[12px] bg-primary hover:bg-primary/90 text-primary-foreground"
                        disabled={!assignBatchId || !!alreadyInBatch || assignLoading}
                        onClick={handleAssign}
                      >
                        {assignLoading ? 'Assigning…' : 'Assign'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

// ── Role tabs bar ─────────────────────────────────────────────────────────────

interface RoleTabsProps {
  active: TabKey;
  counts: Record<TabKey, number>;
  onChange: (tab: TabKey) => void;
}

export function RoleTabs({ active, counts, onChange }: RoleTabsProps) {
  return (
    <Tabs value={active}>
      <TabsList className="inline-flex h-auto gap-1 rounded-lg bg-muted/50 p-1">
        {TAB_ORDER.map((tab) => (
          <TabsTrigger
            key={tab}
            value={tab}
            onClick={() => onChange(tab)}
            className="gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            {TAB_LABEL[tab]}
            <Badge
              variant="secondary"
              className="rounded-full px-1.5 py-0 text-[11px] font-semibold tabular-nums"
            >
              {counts[tab]}
            </Badge>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function fmtDate(d: Date) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function toLocalDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type SortCol = 'username' | 'email' | 'since' | 'org' | 'batch';

function UsersPage() {
  const { status: urlStatus, org: urlOrg, batch: urlBatch, tab: urlTab } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/users' });
  const [orgs, setOrgs]               = useState<OrgSummary[]>([]);
  const [batches, setBatches]         = useState<BatchSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState(urlOrg ?? '');
  const [selectedBatch, setSelectedBatch] = useState(urlBatch ?? '');
  const [users, setUsers]             = useState<AdminUserRow[]>([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [inviteOpen, setInviteOpen]   = useState(false);
  const [activeTab, setActiveTab]     = useState<TabKey>(normalizeTab(urlTab));
  const [statusFilter, setStatusFilter] = useState(urlStatus ?? '');
  const [sortCol, setSortCol]         = useState<SortCol | null>(null);
  const [sortDir, setSortDir]         = useState<'asc' | 'desc'>('asc');
  const [sinceRange, setSinceRange]   = useState<DateRange | undefined>(undefined);
  const [selectedUser, setSelectedUser] = useState<AdminUserRow | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    adminApi.listOrgs()
      .then(setOrgs)
      .catch(() => toast.error('Failed to load organizations'));
  }, []);

  useEffect(() => {
    adminApi.listBatches(selectedOrg || undefined)
      .then(setBatches)
      .catch(() => toast.error('Failed to load batches'));
  }, [selectedOrg]);

  function loadUsers() {
    setLoading(true);
    adminApi.listUsers(selectedOrg || undefined, selectedBatch || undefined)
      .then(setUsers)
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadUsers(); }, [selectedOrg, selectedBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleOrgChange(val: string) {
    setSelectedOrg(val);
    setSelectedBatch('');
    setPage(1);
    navigate({ search: (prev) => ({ ...prev, org: val || undefined, batch: undefined }) });
  }

  function handleBatchChange(val: string) {
    setSelectedBatch(val);
    setPage(1);
    navigate({ search: (prev) => ({ ...prev, batch: val || undefined }) });
  }

  function handleTabChange(tab: TabKey) {
    setActiveTab(tab);
    setPage(1);
    navigate({ search: (prev) => ({ ...prev, tab: tab === 'students' ? undefined : tab }) });
  }

  function handleStatusChange(val: string) {
    setStatusFilter(val);
    setPage(1);
    navigate({ search: (prev) => ({ ...prev, status: val || undefined }) });
  }

  function toggleSort(col: SortCol) {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else setSortCol(null);
    setPage(1);
  }

  const tabCounts = countByRole(users);
  const filtered = filterUsersForTab(users, activeTab, search, statusFilter)
    .filter((u) => !sinceRange?.from || u.created_at.slice(0, 10) >= toLocalDateStr(sinceRange.from))
    .filter((u) => !sinceRange?.to   || u.created_at.slice(0, 10) <= toLocalDateStr(sinceRange.to));

  const sorted = [...filtered].sort((a, b) => {
    if (!sortCol) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortCol) {
      case 'username': return a.username.localeCompare(b.username) * dir;
      case 'email':    return a.email.localeCompare(b.email) * dir;
      case 'since':    return a.created_at.localeCompare(b.created_at) * dir;
      case 'org':      return (a.org_name || '').localeCompare(b.org_name || '') * dir;
      case 'batch':    return (a.batch_name || '').localeCompare(b.batch_name || '') * dir;
    }
  });

  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const colSpan = 7;

  function exportCSV() {
    const headers = ['Username', 'Email', 'Role', 'Status', 'Batch', 'Organization', 'Since'];
    const escVal = (v: string) => `"${(v || '').replace(/"/g, '""')}"`;
    const rows = sorted.map((u) => [
      u.username, u.email, u.role, u.status, u.batch_name, u.org_name,
      formatTimestamp(u.created_at),
    ].map(escVal).join(','));
    const csv = [headers.map(escVal).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function SortHead({ col, label, className }: { col: SortCol; label: string; className?: string }) {
    const active = sortCol === col;
    return (
      <TableHead
        className={cn(
          'text-[11px] font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors',
          className,
        )}
        onClick={() => toggleSort(col)}
      >
        <span className="flex items-center gap-1">
          {label}
          {active ? (
            sortDir === 'asc'
              ? <ChevronUp className="h-3 w-3 text-foreground" />
              : <ChevronDown className="h-3 w-3 text-foreground" />
          ) : (
            <ArrowUpDown className="h-3 w-3 opacity-50" />
          )}
        </span>
      </TableHead>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Controls bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Role tabs */}
        <RoleTabs active={activeTab} counts={tabCounts} onChange={handleTabChange} />

        {/* Divider */}
        <div className="h-5 w-px bg-border mx-1 shrink-0" />

        {/* Right-side actions — flex-1 spacer pushes them to the right */}
        <div className="flex-1" />
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-[190px] pl-9"
              aria-label="Search users"
            />
          </div>

          <Button
            variant="outline"
            onClick={exportCSV}
            disabled={sorted.length === 0}
            className="gap-1.5"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>

          <Button
            onClick={() => setInviteOpen(true)}
            className="gap-1.5 bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="h-4 w-4" /> Invite User
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card shadow-sm [overflow:clip]">
        <table className="w-full caption-bottom text-sm">
          <TableHeader className="sticky -top-6 z-10">
            <TableRow className="bg-[#f5f5f5] hover:bg-[#f5f5f5]">
              <SortHead col="username" label="Username" />
              <SortHead col="email" label="Email" />
              {/* Status — inline filter popover */}
              <TableHead className="w-[160px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                <span className="flex items-center gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider">Status</span>
                  <Popover modal>
                    <PopoverTrigger asChild>
                      {statusFilter ? (
                        <button className="ml-1 flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors capitalize">
                          {statusFilter}
                          <span className="ml-0.5 hover:text-primary/60" onClick={(e) => { e.stopPropagation(); handleStatusChange(''); }}>
                            <X className="h-2.5 w-2.5" />
                          </span>
                        </button>
                      ) : (
                        <button className="ml-0.5 rounded p-0.5 hover:bg-muted transition-colors text-muted-foreground">
                          <Filter className="h-3 w-3" />
                        </button>
                      )}
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-36 p-1">
                      {['active', 'invited', 'expired', 'dropped', 'completed', 'cancelled'].map((s) => (
                        <PopoverClose asChild key={s}>
                          <button
                            className={cn(
                              'w-full rounded px-2 py-1.5 text-left text-[12px] capitalize hover:bg-muted transition-colors',
                              statusFilter === s && 'bg-primary/10 text-primary font-medium',
                            )}
                            onClick={() => handleStatusChange(statusFilter === s ? '' : s)}
                          >
                            {s}
                          </button>
                        </PopoverClose>
                      ))}
                    </PopoverContent>
                  </Popover>
                </span>
              </TableHead>
              {/* Since — sort + date range picker */}
              <TableHead className="w-[200px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                <span className="flex items-center gap-1">
                  <span className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors" onClick={() => toggleSort('since')}>
                    Since
                    {sortCol === 'since' ? (
                      sortDir === 'asc' ? <ChevronUp className="h-3 w-3 text-foreground" /> : <ChevronDown className="h-3 w-3 text-foreground" />
                    ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                  </span>
                  <Popover modal>
                    <PopoverTrigger asChild>
                      {sinceRange?.from && sinceRange.to ? (
                        <button className="ml-1 flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors whitespace-nowrap">
                          {fmtDate(sinceRange.from)} – {fmtDate(sinceRange.to)}
                          <span
                            className="ml-0.5 hover:text-primary/60"
                            onClick={(e) => { e.stopPropagation(); setSinceRange(undefined); }}
                          >
                            <X className="h-2.5 w-2.5" />
                          </span>
                        </button>
                      ) : (
                        <button className={cn('ml-0.5 rounded p-0.5 hover:bg-muted transition-colors', sinceRange?.from && 'text-primary')}>
                          <CalendarDays className="h-3 w-3" />
                        </button>
                      )}
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0">
                      <div className="p-2 border-b flex items-center justify-between">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Joined between</p>
                        {sinceRange?.from && (
                          <button className="text-[10px] text-muted-foreground hover:text-foreground transition-colors" onClick={() => setSinceRange(undefined)}>
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="flex">
                        <Calendar mode="range" selected={sinceRange} onSelect={setSinceRange} numberOfMonths={1} />
                        <div className="flex flex-col gap-0.5 border-l border-border p-2 w-[140px] shrink-0">
                          <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Quick pick</p>
                          {([
                            { label: 'Last 1 month',  range: () => { const t = new Date(); return { from: subMonths(t, 1), to: t }; } },
                            { label: 'Last 2 months', range: () => { const t = new Date(); return { from: subMonths(t, 2), to: t }; } },
                            { label: 'Last 3 months', range: () => { const t = new Date(); return { from: subMonths(t, 3), to: t }; } },
                            { label: 'This month',    range: () => { const t = new Date(); return { from: startOfMonth(t), to: endOfMonth(t) }; } },
                            { label: 'Next 1 month',  range: () => { const t = new Date(); return { from: t, to: addMonths(t, 1) }; } },
                            { label: 'Next 2 months', range: () => { const t = new Date(); return { from: t, to: addMonths(t, 2) }; } },
                          ] as const).map((p) => (
                            <button
                              key={p.label}
                              type="button"
                              onClick={() => setSinceRange(p.range())}
                              className="rounded-md px-2.5 py-1.5 text-left text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="p-2 border-t flex justify-end">
                        <PopoverClose asChild>
                          <Button size="sm" variant="outline" className="h-7 text-xs">Done</Button>
                        </PopoverClose>
                      </div>
                    </PopoverContent>
                  </Popover>
                </span>
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                <span className="flex items-center gap-1">
                  <span
                    className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                    onClick={() => toggleSort('org')}
                  >
                    Organization
                    {sortCol === 'org' ? (
                      sortDir === 'asc' ? <ChevronUp className="h-3 w-3 text-foreground" /> : <ChevronDown className="h-3 w-3 text-foreground" />
                    ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                  </span>
                  <Popover modal>
                    <PopoverTrigger asChild>
                      {selectedOrg ? (
                        <button className="ml-1 flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors max-w-[120px]">
                          <span className="truncate">{orgs.find((o) => o.id === selectedOrg)?.name ?? 'Org'}</span>
                          <span className="ml-0.5 hover:text-primary/60" onClick={(e) => { e.stopPropagation(); handleOrgChange(''); }}>
                            <X className="h-2.5 w-2.5" />
                          </span>
                        </button>
                      ) : (
                        <button className="ml-0.5 rounded p-0.5 hover:bg-muted transition-colors text-muted-foreground">
                          <Filter className="h-3 w-3" />
                        </button>
                      )}
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-52 p-1">
                      {orgs.map((o) => (
                        <PopoverClose asChild key={o.id}>
                          <button
                            className={cn(
                              'w-full rounded px-2 py-1.5 text-left text-[12px] hover:bg-muted transition-colors',
                              selectedOrg === o.id && 'bg-primary/10 text-primary font-medium',
                            )}
                            onClick={() => handleOrgChange(selectedOrg === o.id ? '' : o.id)}
                          >
                            {o.name}
                          </button>
                        </PopoverClose>
                      ))}
                    </PopoverContent>
                  </Popover>
                </span>
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                <span className="flex items-center gap-1">
                  <span
                    className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                    onClick={() => toggleSort('batch')}
                  >
                    Batch
                    {sortCol === 'batch' ? (
                      sortDir === 'asc' ? <ChevronUp className="h-3 w-3 text-foreground" /> : <ChevronDown className="h-3 w-3 text-foreground" />
                    ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                  </span>
                  <Popover modal>
                    <PopoverTrigger asChild>
                      {selectedBatch ? (
                        <button className="ml-1 flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors max-w-[120px]">
                          <span className="truncate">{batches.find((b) => b.id === selectedBatch)?.name ?? 'Batch'}</span>
                          <span className="ml-0.5 hover:text-primary/60" onClick={(e) => { e.stopPropagation(); handleBatchChange(''); }}>
                            <X className="h-2.5 w-2.5" />
                          </span>
                        </button>
                      ) : (
                        <button className="ml-0.5 rounded p-0.5 hover:bg-muted transition-colors text-muted-foreground">
                          <Filter className="h-3 w-3" />
                        </button>
                      )}
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-56 p-1 max-h-64 overflow-y-auto">
                      {batches.length === 0 ? (
                        <p className="px-2 py-1.5 text-[12px] text-muted-foreground">No batches</p>
                      ) : batches.map((b) => (
                        <PopoverClose asChild key={b.id}>
                          <button
                            className={cn(
                              'w-full rounded px-2 py-1.5 text-left text-[12px] hover:bg-muted transition-colors',
                              selectedBatch === b.id && 'bg-primary/10 text-primary font-medium',
                            )}
                            onClick={() => handleBatchChange(selectedBatch === b.id ? '' : b.id)}
                          >
                            {b.name}
                          </button>
                        </PopoverClose>
                      ))}
                    </PopoverContent>
                  </Popover>
                </span>
              </TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [1, 2, 3, 4].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: colSpan }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-[70%]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <EmptyState
                    icon={Users}
                    title={`No ${TAB_LABEL[activeTab].toLowerCase()} found`}
                    description="Try adjusting your filters."
                    className="py-12"
                  />
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((u) => (
                <UserTableRow
                  key={u.user_id || u.invite_id}
                  u={u}
                  showOrg
                  showBatch
                  onSelect={setSelectedUser}
                  onDelete={async (userID) => {
                    if (!userID) { loadUsers(); return; }
                    try {
                      await adminApi.deleteUser(userID);
                      toast.success('User deleted');
                      loadUsers();
                    } catch {
                      toast.error('Failed to delete user');
                    }
                  }}
                />
              ))
            )}
          </TableBody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-[13px] text-muted-foreground">
          <span>
            {sorted.length === 0 ? 'No results' : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, sorted.length)} of ${sorted.length}`}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[12px]"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span className="px-2">Page {safePage} of {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[12px]"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <InviteUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        orgs={orgs}
        initialOrgId={selectedOrg}
        initialBatchId={selectedBatch}
        initialBatches={batches}
      />

      <UserDetailPanel user={selectedUser} onClose={() => setSelectedUser(null)} onAssigned={loadUsers} />
    </div>
  );
}

// ── Role badge ───────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  const colorMap: Record<string, string> = {
    trainer:     'border-emerald-500 text-emerald-600',
    student:     'border-orange-500 text-orange-600',
    org_admin:   'border-primary text-primary',
    super_admin: 'border-purple-500 text-purple-600 dark:text-purple-400',
  };
  const colors = colorMap[role] ?? 'border-gray-400 text-gray-600';
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium bg-transparent capitalize', colors)}>
      {role.replace('_', ' ')}
    </span>
  );
}

// ── Status display ───────────────────────────────────────────────────────────

function StatusDisplay({ status }: { status: string | undefined }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const colorMap: Record<string, string> = {
    active:    'text-emerald-600',
    invited:   'text-blue-600',
    dropped:   'text-red-600',
    completed: 'text-gray-500',
    ended:     'text-gray-500',
    cancelled: 'text-red-600',
    expired:   'text-orange-600',
  };
  return (
    <span className={cn('text-[13px] font-medium capitalize', colorMap[status] ?? 'text-muted-foreground')}>
      {status}
    </span>
  );
}

// ── User row ─────────────────────────────────────────────────────────────────

function UserTableRow({
  u,
  showOrg,
  showBatch,
  onSelect,
  onDelete,
}: {
  u: AdminUserRow;
  showOrg: boolean;
  showBatch: boolean;
  onSelect: (u: AdminUserRow) => void;
  onDelete: (userID: string) => Promise<void>;
}) {
  const [menuOpen, setMenuOpen]           = useState(false);
  const [resending, setResending]         = useState(false);
  const [deletingUser, setDeletingUser]   = useState(false);
  const [deletingInvite, setDeletingInvite] = useState(false);

  const isPendingInvite = Boolean(u.invite_id);

  async function handleResendInvite() {
    if (!u.invite_id) return;
    setResending(true);
    try {
      const res = await adminApi.resendInvite(u.invite_id);
      await navigator.clipboard.writeText(res.invite_url);
      toast.success('Invite link copied & resent', { description: u.email });
      setMenuOpen(false);
    } catch {
      toast.error('Failed to resend invite');
    } finally {
      setResending(false);
    }
  }

  function copyEmail(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(u.email);
    toast.success('Email copied');
  }

  return (
    <TableRow
      className="hover:bg-muted/30 cursor-pointer"
      onClick={() => { if (!isPendingInvite) onSelect(u); }}
    >
      <TableCell className="text-[13px] font-medium text-foreground hover:text-primary">
        {u.username || (
          <span className="text-muted-foreground italic">
            {isPendingInvite ? u.email.split('@')[0] : 'Not set up'}
          </span>
        )}
      </TableCell>
      <TableCell
        className="text-[13px] text-muted-foreground cursor-copy group"
        onClick={copyEmail}
        title="Click to copy email"
      >
        <span className="flex items-center gap-1">
          {u.email}
          <Copy className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity shrink-0" />
        </span>
      </TableCell>
      <TableCell>
        <StatusDisplay status={u.status} />
      </TableCell>
      <TableCell className="text-[12px] text-muted-foreground">
        {formatTimestamp(u.created_at)}
      </TableCell>
      {showOrg && (
        <TableCell className="text-[12px] text-muted-foreground">
          {u.org_id && u.org_name ? (
            <Link
              to="/admin/orgs/$orgId"
              params={{ orgId: u.org_id }}
              onClick={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-primary hover:underline"
            >
              {u.org_name}
            </Link>
          ) : (
            u.org_name || <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
      )}
      {showBatch && (
        <TableCell className="text-[12px] text-muted-foreground">
          <span className="flex items-center gap-1.5 flex-wrap">
            {u.batch_id && u.batch_name ? (
              <Link
                to="/admin/batches/$batchId"
                params={{ batchId: u.batch_id }}
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-primary hover:underline"
              >
                {u.batch_name}
              </Link>
            ) : (
              u.batch_name || <span className="text-muted-foreground">—</span>
            )}
            {u.batch_count > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onSelect(u); }}
                className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
              >
                +{u.batch_count - 1} more
              </button>
            )}
          </span>
        </TableCell>
      )}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
              aria-label="Open actions menu"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {deletingUser || deletingInvite ? (
              <div className="flex items-center gap-2 px-2 py-1.5">
                <button
                  type="button"
                  onClick={async () => {
                    if (deletingInvite && u.invite_id) {
                      setDeletingInvite(false);
                      try {
                        await adminApi.deleteInvite(u.invite_id);
                        toast.success('Invite deleted');
                        await onDelete('');
                      } catch {
                        toast.error('Failed to delete invite');
                      }
                    } else {
                      setDeletingUser(false);
                      await onDelete(u.user_id);
                    }
                  }}
                  className="text-[12px] font-medium text-red-600 hover:text-red-700"
                >
                  Confirm delete?
                </button>
                <button
                  type="button"
                  onClick={() => { setDeletingUser(false); setDeletingInvite(false); }}
                  className="text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            ) : isPendingInvite ? (
              <>
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); handleResendInvite(); }}
                  disabled={resending}
                  className="gap-2"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {resending ? 'Sending…' : 'Resend link'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setDeletingInvite(true); }}
                  className="text-red-600 focus:text-red-700 gap-2"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete invite
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); setDeletingUser(true); }}
                className="text-red-600 focus:text-red-700 gap-2"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete user
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
